import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  suggestionsAPI,
  apiResource,
  partnerColorsAPI,
} from "../../services/api";
import type { PartnerColor } from "../../constants/partnerColors";
import {
  PartnerInfoWithClientAndReg,
  PartnerState,
  UpdatePartnerInfoWithClientCode,
} from "../../types";

const partnersAPI = apiResource("partners");

const initialState: PartnerState = {
  partners: [],
  currentPartner: null,
  suggestions: [],
  loading: false,
  error: null,
  totalFound: 0,
  searchQuery: "",
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

// Get Partner Suggestions
export const getPartnerSuggestions = createAsyncThunk(
  "partner/getPartnerSuggestions",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await suggestionsAPI.getPartners(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch partner suggestions"
      );
    }
  }
);

export const getPartners = createAsyncThunk(
  "partner/getPartners",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await partnersAPI.getAll(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch partners"
      );
    }
  }
);

export const getPartner = createAsyncThunk(
  "partner/getPartner",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await partnersAPI.getOne(id);
      return response.data.data.partner;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch partner"
      );
    }
  }
);

export const createPartner = createAsyncThunk(
  "partner/createPartner",
  async (partnerData: PartnerInfoWithClientAndReg, { rejectWithValue }) => {
    try {
      const response = await partnersAPI.create(partnerData);
      return response.data.data.partner;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create partner"
      );
    }
  }
);

export const updatePartner = createAsyncThunk(
  "partner/updatePartner",
  async (
    {
      id,
      partnerData,
    }: { id: string; partnerData: UpdatePartnerInfoWithClientCode },
    { rejectWithValue }
  ) => {
    try {
      const response = await partnersAPI.update(id, partnerData);
      return response.data.data.partner;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update partner"
      );
    }
  }
);

/**
 * Change a partner's colour on its own, from the partner's header.
 *
 * Not `updatePartner`: the colour is picked while the form may be open with
 * unsaved edits, and the server deliberately leaves the record's version alone
 * for this write so that form can still save afterwards.
 */
export const setPartnerColor = createAsyncThunk(
  "partner/setPartnerColor",
  async (
    { id, color }: { id: string; color: PartnerColor | null },
    { rejectWithValue }
  ) => {
    try {
      const response = await partnerColorsAPI.set(id, color);
      return response.data.data.partner;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update partner colour"
      );
    }
  }
);

export const deletePartner = createAsyncThunk(
  "partner/deletePartner",
  async (id: string, { rejectWithValue }) => {
    try {
      await partnersAPI.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete partner"
      );
    }
  }
);

const partnerSlice = createSlice({
  name: "partner",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
      state.totalFound = 0;
      state.searchQuery = "";
    },
    clearCurrentPartner: (state) => {
      state.currentPartner = null;
    },
    resetPartnerState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Partner Suggestions
      .addCase(getPartnerSuggestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPartnerSuggestions.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestions = action.payload.suggestions;
        state.totalFound = action.payload.totalFound;
        state.searchQuery = action.payload.searchQuery;
        state.error = null;
      })
      .addCase(getPartnerSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.suggestions = [];
        state.totalFound = 0;
      })
      // Get Partners
      .addCase(getPartners.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        // Mark this as the newest in-flight list request.
        state.latestListRequestId = action.meta.requestId;
      })
      .addCase(getPartners.fulfilled, (state, action) => {
        // Drop out-of-order responses: only the newest request may write the
        // list. Prevents a slow Partners fetch from flashing over Forwarders
        // after a quick tab switch.
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.loading = false;
        state.partners = action.payload.partners;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getPartners.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestListRequestId) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Partner
      .addCase(getPartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPartner.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPartner = action.payload;
        state.error = null;
      })
      .addCase(getPartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create Partner
      .addCase(createPartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPartner.fulfilled, (state) => {
        // No optimistic list insert: the caller refetches the active tab so the
        // new record only shows in the tab it actually belongs to (partner vs
        // forwarder). Avoids showing a just-created forwarder on the Partners tab.
        state.loading = false;
        state.error = null;
      })
      .addCase(createPartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Partner
      .addCase(updatePartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePartner.fulfilled, (state, action) => {
        // Don't patch the row in place — toggling isForwarder can move it out of
        // the current tab, so the caller refetches. Keep currentPartner in sync
        // for any open detail view.
        state.loading = false;
        state.error = null;
        if (state.currentPartner?._id === action.payload._id) {
          state.currentPartner = action.payload;
        }
      })
      .addCase(updatePartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Set Partner Colour — patched in place, unlike a full update: the colour
      // cannot move a row between tabs. Deliberately no `loading` flag, so the
      // open form underneath does not flash into its busy state.
      .addCase(setPartnerColor.fulfilled, (state, action) => {
        const { _id, color } = action.payload;
        const row = state.partners.find((partner) => partner._id === _id);
        if (row) row.color = color;
        if (state.currentPartner && state.currentPartner._id === _id) {
          state.currentPartner.color = color;
        }
      })
      // Delete Partner
      .addCase(deletePartner.fulfilled, (state, action) => {
        state.partners = state.partners.filter(
          (partner) => partner._id !== action.payload
        );
        state.pagination.total -= 1;
        state.loading = false;
        state.error = null;
      })
      .addCase(deletePartner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePartner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuggestions, resetPartnerState } =
  partnerSlice.actions;
export default partnerSlice.reducer;
